import { useState, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { getTrainingAuditLog } from '../../../services/trainingService';
import { dateFilterColumn } from '../../../utils/dateFilterColumn';
import { formatDateTime } from '../../../utils/Utils';
import PersonCell from '../../../components/ui/PersonCell';

const useTrainingAuditApi = (queryParams) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  const [fetching, setFetching] = useState(true);
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({ totalRowCount: 0, pageSize: 50, pageIndex: 0 });
  const lastValidQueryParams = useRef(null);

  useEffect(() => {
    const paramsToUse = queryParams && Object.keys(queryParams).length > 0
      ? queryParams
      : lastValidQueryParams.current || { pageNumber: 1, pageSize: 50 };
    lastValidQueryParams.current = paramsToUse;
    setFetching(true);
    getTrainingAuditLog(paramsToUse)
      .then((res) => {
        const result = res.data?.data;
        const items = Array.isArray(result) ? result : (result?.data || []);
        setData(items);
        setPagination({
          totalRowCount: result?.totalRowCount || items.length,
          pageIndex: result?.pageIndex ? result.pageIndex - 1 : 0,
          pageSize: result?.pageSize || items.length || 50,
        });
      })
      .finally(() => setFetching(false));
  }, [queryParams]);

  // Backend audit logs only use these two prefixes; the dropdown filter
  // sends `action[startswith]=training.` (or `assignment.`). Keeping the
  // option list short avoids inventing labels that don't map to anything.
  const actionPrefixOptions = useMemo(() => [
    { id: 'training.', name: t('training.title') },
    { id: 'assignment.', name: t('training.assignedUsers') },
  ], [t]);

  const actionPrefixLabel = (val) => actionPrefixOptions.find((o) => o.id === val)?.name || val;

  // Map raw audit action codes to translated labels.
  const actionLabel = (code) => {
    if (!code) return '-';
    const key = `auditActions.${code}`;
    const translated = t(key);
    return translated === key ? code : translated;
  };

  const mandatoryOptions = useMemo(() => [
    { id: 'true', name: t('common.yes') },
    { id: 'false', name: t('common.no') },
  ], [t]);

  const columns = useMemo(() => [
    { name: 'id', label: 'ID', options: { filter: false, sort: false, display: 'excluded' } },
    {
      name: 'timestamp',
      label: t('audit.timestamp'),
      options: {
        filter: false, sort: false,
        customBodyRender: (v) => formatDateTime(v, lang),
      },
    },
    {
      name: 'action',
      label: t('audit.action'),
      options: {
        filter: true, sort: false,
        filterType: 'dropdown',
        filterOptions: {
          names: actionPrefixOptions.map((o) => o.id),
          defaultOp: 'startswith',
          renderValue: (v) => actionPrefixLabel(v),
        },
        customFilterListOptions: { render: (v) => `${t('audit.action')}: ${actionPrefixLabel(v)}` },
        customBodyRender: (v) => actionLabel(v),
      },
    },
    {
      name: 'userFullName',
      label: t('audit.actor'),
      options: {
        filter: false, sort: false,
        customBodyRender: (v, tableMeta) => (
          v ? <PersonCell name={v} profilePicture={data[tableMeta.rowIndex]?.userProfilePicture} /> : '-'
        ) },
    },
    {
      name: 'trainingTitle',
      label: t('training.title'),
      options: {
        filter: true, sort: false,
        filterType: 'textField',
        filterOptions: { defaultOp: 'like' },
        customBodyRender: (v) => v || '-',
      },
    },
    {
      name: 'isMandatory',
      label: t('training.isMandatory'),
      options: {
        filter: true, sort: false,
        filterType: 'dropdown',
        filterOptions: {
          names: mandatoryOptions.map((o) => o.id),
          defaultOp: 'eq',
          renderValue: (v) => mandatoryOptions.find((o) => o.id === v)?.name || v,
        },
        customFilterListOptions: { render: (v) => `${t('training.isMandatory')}: ${mandatoryOptions.find((o) => o.id === v)?.name || v}` },
        customBodyRender: (v) => v == null ? '-' : (v ? t('common.yes') : t('common.no')),
      },
    },
    {
      name: 'categoryName',
      label: t('training.category'),
      options: {
        filter: true, sort: false,
        filterType: 'textField',
        filterOptions: { defaultOp: 'like' },
        customBodyRender: (v) => v || '-',
      },
    },
    dateFilterColumn('date', t('audit.timestamp'), lang),
  ], [t, lang, actionPrefixOptions, mandatoryOptions, data]);

  return { columns, data, fetching, pagination };
};

export default useTrainingAuditApi;
